import { IAccountDetailedRow, ReportColumnNames, ReportExtensions } from '@spa/api';

import { ReportTableColumn, ReportTableData } from '../../../app/shared/components';

export const DetailedReportTradesColumns = (): ReportTableColumn[] => {
    return [
        new ReportTableColumn({
            label: 'TIME',
            selector: ReportColumnNames.Time,
            sortIndex: 0
        }),
        new ReportTableColumn({
            label: 'ORDER ID',
            selector: ReportColumnNames.OrderId,
            sortIndex: 1
        }),
        new ReportTableColumn({
            label: 'FILL ID',
            selector: ReportColumnNames.FillId,
            sortIndex: 2,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'ROUTE',
            selector: ReportColumnNames.Route,
            sortIndex: 3
        }),
        new ReportTableColumn({
            label: 'LIQ',
            selector: ReportColumnNames.Liq,
            sortIndex: 4
        }),
        new ReportTableColumn({
            label: '',
            selector: ReportColumnNames.Type,
            sortIndex: 5
        }),
        new ReportTableColumn({
            label: 'QTY',
            selector: ReportColumnNames.Qty,
            sortIndex: 6,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'PRICE',
            selector: ReportColumnNames.Price,
            sortIndex: 7,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'POSITION',
            selector: ReportColumnNames.Position,
            sortIndex: 8,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'GROSS',
            selector: ReportColumnNames.Gross,
            sortIndex: 9,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'COMM',
            selector: ReportColumnNames.COMM,
            sortIndex: 10,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'ECN FEE',
            selector: ReportColumnNames.ECNFee,
            sortIndex: 11,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'SEC',
            selector: ReportColumnNames.SEC,
            sortIndex: 12,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'TAF',
            selector: ReportColumnNames.TAF,
            sortIndex: 13,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'NSCC',
            selector: ReportColumnNames.NSCC,
            sortIndex: 14,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'CLR',
            selector: ReportColumnNames.CLR,
            sortIndex: 15,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'ORF',
            selector: ReportColumnNames.ORF,
            sortIndex: 16,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'PTFPF',
            selector: ReportColumnNames.PTFPF,
            sortIndex: 17,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'EFTT',
            selector: ReportColumnNames.EFTT,
            sortIndex: 18,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'CAT',
            selector: ReportColumnNames.CAT,
            sortIndex: 19,
            shifted: true
        }),
        new ReportTableColumn({
            label: 'NET',
            selector: ReportColumnNames.Net,
            sortIndex: 20,
            shifted: true
        })
    ];
};

export const DetailedReportSymbolTotalDataItems = (
    label: string,
    total?: IAccountDetailedRow
): ReportTableData[] => {
    const getReportTableDataForDecimal = GetReportTableDataForDecimalFactory(total, getFormattedDecimal);

    return [
        ...GetTotalTypeData(label, 3),
        getReportTableDataForInteger(ReportColumnNames.Orders, 'ORDERS'),

        new ReportTableData({
            value: '',
            colId: '',
            hidden: false
        }),

        getReportTableDataForInteger(ReportColumnNames.Fills, 'FILLS'),
        getReportTableDataForDecimal(ReportColumnNames.Qty, 'QTY', 3, true),
        getReportTableDataForDecimal(ReportColumnNames.Price, 'PRICE', 4),
        getReportTableDataForInteger(ReportColumnNames.Position, 'POSITION'),
        getReportTableDataForDecimal(ReportColumnNames.Gross, 'GROSS', 2),
        getReportTableDataForDecimal(ReportColumnNames.COMM, 'COMM'),
        getReportTableDataForDecimal(ReportColumnNames.ECNFee, 'ECN FEE'),
        getReportTableDataForDecimal(ReportColumnNames.SEC, 'SEC'),
        getReportTableDataForDecimal(ReportColumnNames.TAF, 'TAF'),
        getReportTableDataForDecimal(ReportColumnNames.NSCC, 'NSCC'),
        getReportTableDataForDecimal(ReportColumnNames.CLR, 'CLR'),
        getReportTableDataForDecimal(ReportColumnNames.ORF, 'ORF'),
        getReportTableDataForDecimal(ReportColumnNames.PTFPF, 'PTFPF'),
        getReportTableDataForDecimal(ReportColumnNames.EFTT, 'EFTT'),
        getReportTableDataForDecimal(ReportColumnNames.CAT, 'CAT'),
        getReportTableDataForDecimal(ReportColumnNames.Net, 'NET', 2),
    ];

    function getReportTableDataForInteger(
        columnName: ReportColumnNames,
        defaultValue: string): ReportTableData {
        return new ReportTableData({
            value: getFormattedInteger(columnName, defaultValue),
            colId: columnName,
            hidden: false,
            hasNegativeValue: !total ? false : ReportExtensions.isNegative(total[columnName]),
            shifted: true
        });
    }

    function getFormattedInteger(
        columnName: ReportColumnNames,
        defaultValue: string): string {
        return typeof total === 'undefined'
            ? defaultValue
            : !total
                ? ''
                : ReportExtensions.toIntegerString(total[columnName]);

    }

    function getFormattedDecimal(
        columnName: ReportColumnNames,
        defaultValue: string,
        maximumFractionDigits = 3,
        minimumFractionDigits = 2): string {
        return typeof total === 'undefined'
            ? defaultValue
            : !total
                ? ''
                : ReportExtensions.toDecimalString(total[columnName], maximumFractionDigits, minimumFractionDigits);

    }
};

export const DetailedReportTotalDataItems = (
    label: string,
    total?: IAccountDetailedRow,
    isTotalFees = false
): ReportTableData[] => {
    const getReportTableDataForDecimal = GetReportTableDataForDecimalFactory(total, getFormattedDecimal);

    return [
        ...GetTotalTypeData(label, 3),
        getReportTableDataForInteger(ReportColumnNames.Orders, 'ORDERS'),
        new ReportTableData({
            value: '',
            colId: '',
            hidden: false
        }),
        getReportTableDataForInteger(ReportColumnNames.Fills, 'FILLS'),
        getReportTableDataForDecimal(ReportColumnNames.Qty, 'QTY', 3, true),
        new ReportTableData({
            value: '',
            colId: ReportColumnNames.Price,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: '',
            colId: ReportColumnNames.Position,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        getReportTableDataForDecimal(ReportColumnNames.Gross, 'GROSS', 2),
        getReportTableDataForDecimal(ReportColumnNames.COMM, 'COMM', 2),
        getReportTableDataForDecimal(ReportColumnNames.ECNFee, 'ECN FEE', 2),
        getReportTableDataForDecimal(ReportColumnNames.SEC, 'SEC', 2),
        getReportTableDataForDecimal(ReportColumnNames.TAF, 'TAF', 2),
        getReportTableDataForDecimal(ReportColumnNames.NSCC, 'NSCC', 2),
        getReportTableDataForDecimal(ReportColumnNames.CLR, 'CLR', 2),
        getReportTableDataForDecimal(ReportColumnNames.ORF, 'ORF', 2),
        getReportTableDataForDecimal(ReportColumnNames.PTFPF, 'PTFPF', 2),
        getReportTableDataForDecimal(ReportColumnNames.EFTT, 'EFTT', 2),
        getReportTableDataForDecimal(ReportColumnNames.CAT, 'CAT', 2),
        getReportTableDataForDecimal(ReportColumnNames.Net, 'NET', 2),
    ];

    function getReportTableDataForInteger(
        columnName: ReportColumnNames,
        defaultValue: string): ReportTableData {
        return new ReportTableData({
            value: getFormattedDecimal(columnName, defaultValue, 0),
            colId: columnName,
            hidden: false,
            hasNegativeValue: !total ? false : ReportExtensions.isNegative(total[columnName]),
            shifted: true
        });
    }

    function getFormattedDecimal(
        columnName: ReportColumnNames,
        defaultValue: string,
        toFixed = 3,
        removeFractionIfInteger = false): string {
        return !total
            ? defaultValue
            : !total || isTotalFees
                ? ''
                : ReportExtensions.toDecimalString(total[columnName], toFixed, toFixed, true, removeFractionIfInteger);
    }
};

export const DetailedReportAdjustmentsDataItems = (label: string, net?: number): ReportTableData[] => {
    return [
        ...GetTotalTypeData(label, 10),
        new ReportTableData({
            value: !net ? 'COMM' : '',
            colId: ReportColumnNames.COMM,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'ECN FEE' : '',
            colId: ReportColumnNames.ECNFee,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'SEC' : '',
            colId: ReportColumnNames.SEC,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'TAF' : '',
            colId: ReportColumnNames.TAF,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'NSCC' : '',
            colId: ReportColumnNames.NSCC,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'CLR' : '',
            colId: ReportColumnNames.CLR,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'ORF' : '',
            colId: ReportColumnNames.ORF,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'PTFPF' : '',
            colId: ReportColumnNames.PTFPF,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'EFTT' : '',
            colId: ReportColumnNames.EFTT,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'CAT' : '',
            colId: ReportColumnNames.CAT,
            hidden: false,
            hasNegativeValue: false,
            shifted: true
        }),
        new ReportTableData({
            value: !net ? 'NET' : ReportExtensions.toDecimalString(net),
            colId: ReportColumnNames.Net,
            hidden: false,
            hasNegativeValue: !net ? false : ReportExtensions.isNegative(net),
            shifted: true
        })
    ];
};

const GetTotalTypeData = (label: string, count: number): ReportTableData[] => {
    return new Array(count).fill(null).map((item, index) => {
        return new ReportTableData({
            value: index > 0 ? '' : label,
            colId: 'TotalType',
            hidden: false,
            hasNegativeValue: false
        });
    });
};

const GetReportTableDataForDecimalFactory = (total: IAccountDetailedRow, getFunction: any) => {
    return function getReportTableDataForDecimal(
        columnName: ReportColumnNames,
        defaultValue: string,
        toFixed = 3,
        removeFractionIfInteger = false): ReportTableData {
        return new ReportTableData({
            value: getFunction(columnName, defaultValue, toFixed, removeFractionIfInteger),
            colId: columnName,
            hidden: false,
            hasNegativeValue: !total ? false : ReportExtensions.isNegative(total[columnName]),
            shifted: true
        });
    };
};
